import { useCallback, useEffect, useState } from "react";
import {
  F01_INSIGHT_POPULATION_BREAKDOWNS,
  F01_INSIGHT_POPULATION_LABELS,
  type F01InsightBreakdown,
  type F01InsightPopulation,
  type F01InsightReport,
} from "@care-by-ella/contracts";
import {
  F01InsightsDisabledError,
  fetchF01Insights,
} from "@/lib/f01-insights-api";
import {
  BREAKDOWN_LABELS,
  formatCell,
  formatShare,
  periodLabel,
} from "@/lib/f01-insights";

// TES-1326 — the F-01 quality and product insight view (Emelie 2026-10-13).
// Every number arrives already suppressed and rounded by the API. The page
// only lays it out. Nothing on it is a link or opens anything: "Det ska inte
// gå att klicka vidare från en siffra till en patientlista."

type Question = F01InsightReport["questions"][number];

const WEIGHT_MED_HISTORY = "weightMedHistory";

// TES-1403 (Ö-542) — the unidentified web answers. Their own purpose text,
// their own rules (20 everywhere), and the note that they are never added to
// the patients.
function WebRules({ report }: { report: F01InsightReport }) {
  return (
    <div className="border border-surfaceMuted rounded-md p-md bg-surface text-xs text-textSecondary flex flex-col gap-xs max-w-3xl">
      <p>
        Webbsvar från personer som ännu inte blivit patienter. De används bara
        aggregerat, för produktutveckling, kvalitet och patientsäkerhet. De får
        aldrig användas för individuell marknadsföring, individuell kommersiell
        profilering eller marknadsurval.
      </p>
      <p>
        Svaren hålls åtskilda från patienternas och räknas aldrig ihop med dem.
        De saknar identitet, så varje webbsvar räknas för sig: samma person kan
        ha svarat flera gånger, och den som senare blivit patient i appen utan
        att svaren kopplats finns även bland patienterna.
      </p>
      <p>
        Varje siffra kräver minst {report.rules.minGroup} personer, fler celler
        döljs så att en dold siffra inte kan räknas fram, antal avrundas till
        närmaste {report.rules.roundTo} och andelar räknas på avrundade antal.
        Vikt, längd, BMI, reproduktiv status, typ 1-diabetes, MTC/MEN2,
        receptövertag och fritext visas inte. Varje visning loggas.
      </p>
    </div>
  );
}

function Rules({ report }: { report: F01InsightReport }) {
  if (report.population === "web_unidentified") {
    return <WebRules report={report} />;
  }
  // TES-1371 — the API includes weightMedHistory only behind its own switch.
  // Without it the page reads exactly as before.
  const withWeightMedHistory = report.questions.some(
    (q) => q.key === WEIGHT_MED_HISTORY,
  );
  if (withWeightMedHistory) {
    return (
      <div className="border border-surfaceMuted rounded-md p-md bg-surface text-xs text-textSecondary flex flex-col gap-xs max-w-3xl">
        <p>
          Siffrorna är aggregerade och används för uppföljning, utvärdering och
          utveckling av vårdtjänsten, patientresan och patientinformationen.
          Grupper med färre än {report.rules.minGroup} personer döljs. Källa och
          livsfas kräver minst {report.rules.minSensitiveGroup}. Antal avrundas
          till närmaste {report.rules.roundTo}.
        </p>
        <p>
          Tidigare receptbelagd viktbehandling är en känsligare variabel. Den
          kräver minst {report.rules.minSensitiveGroup} personer i varje grupp,
          fler celler döljs så att en dold siffra inte kan räknas fram, andelar
          räknas på avrundade antal och den ingår inte i svarskombinationerna.
        </p>
        <p>
          Vikt, längd, BMI, reproduktiv status, typ 1-diabetes, MTC/MEN2,
          receptövertag och fritext visas inte. Varje visning loggas. Resultaten
          får inte användas för individuell annonsering, retargeting,
          e-postsegmentering, direktkontakt eller segmentering.
        </p>
      </div>
    );
  }
  return (
    <div className="border border-surfaceMuted rounded-md p-md bg-surface text-xs text-textSecondary flex flex-col gap-xs max-w-3xl">
      <p>
        Siffrorna är aggregerade och används för uppföljning, utvärdering och
        utveckling av vårdtjänsten, patientresan och patientinformationen.
        Grupper med färre än {report.rules.minGroup} personer döljs. Källa och
        livsfas kräver minst {report.rules.minSensitiveGroup}. Antal avrundas
        till närmaste {report.rules.roundTo}.
      </p>
      <p>
        Vikt, längd, BMI, reproduktiv status, typ 1-diabetes, MTC/MEN2,
        receptövertag, tidigare läkemedelsbehandling och fritext visas inte.
        Varje visning loggas. Resultaten får inte användas för individuell
        annonsering, retargeting, direktkontakt eller segmentering.
      </p>
    </div>
  );
}

function QuestionTable({
  question,
  groups,
}: {
  question: Question;
  groups: F01InsightReport["groups"];
}) {
  const label = (key: string) =>
    groups.find((g) => g.key === key)?.label ?? key;
  return (
    <section className="flex flex-col gap-xs" data-f01-question={question.key}>
      <h2 className="text-sm font-medium">
        {question.label}
        <span className="text-textSecondary font-normal">
          {question.kind === "multi" ? " · flera val möjliga" : ""}
          {question.sensitive ? " · minst 20 per grupp" : ""}
        </span>
      </h2>
      <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
              <th className="px-md py-sm font-medium">Svar</th>
              {question.groups.map((g) => (
                <th
                  key={g.group}
                  className="px-md py-sm font-medium text-right"
                >
                  {label(g.group)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {question.groups[0]?.options.map((o, i) => (
              <tr key={o.value} className="border-t border-surfaceMuted">
                <td className="px-md py-sm break-words">{o.label}</td>
                {question.groups.map((g) => (
                  <td
                    key={g.group}
                    className="px-md py-sm text-right tabular-nums whitespace-nowrap"
                  >
                    {g.notAsked ? (
                      <span className="text-textSecondary">Ställdes inte</span>
                    ) : g.hidden || g.options[i].count.n === null ? (
                      <span className="text-textSecondary">
                        {formatCell(g.options[i].count)}
                      </span>
                    ) : (
                      <>
                        {formatShare(g.options[i].share)}
                        <span className="text-textSecondary">
                          {" "}
                          ({formatCell(g.options[i].count)})
                        </span>
                      </>
                    )}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-t border-surfaceMuted text-textSecondary">
              <td className="px-md py-sm">Har svarat</td>
              {question.groups.map((g) => (
                <td
                  key={g.group}
                  className="px-md py-sm text-right tabular-nums"
                >
                  {g.notAsked ? "–" : formatCell(g.answered)}
                </td>
              ))}
            </tr>
            <tr className="border-t border-surfaceMuted text-textSecondary">
              <td className="px-md py-sm">Inte svarat (bortfall)</td>
              {question.groups.map((g) => (
                <td
                  key={g.group}
                  className="px-md py-sm text-right tabular-nums"
                >
                  {g.notAsked
                    ? "–"
                    : g.nonResponse.n === null
                      ? formatCell(g.nonResponse)
                      : `${formatCell(g.nonResponse)} (${formatShare(g.nonResponseShare)})`}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Pairs({ pairs }: { pairs: F01InsightReport["pairs"] }) {
  if (pairs.length === 0) {
    return (
      <p className="text-sm text-textSecondary border border-surfaceMuted rounded-md p-md bg-surface">
        Inga svarspar är tillräckligt vanliga för att visas under perioden.
      </p>
    );
  }
  return (
    <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
            <th className="px-md py-sm font-medium">Svar</th>
            <th className="px-md py-sm font-medium">Tillsammans med</th>
            <th className="px-md py-sm font-medium text-right">
              Andel av alla
            </th>
          </tr>
        </thead>
        <tbody>
          {pairs.map((p) => (
            <tr
              key={`${p.a.question}:${p.a.value}|${p.b.question}:${p.b.value}`}
              className="border-t border-surfaceMuted"
            >
              <td className="px-md py-sm break-words">
                {p.a.label}
                <span className="block text-xs text-textSecondary">
                  {p.a.questionLabel}
                </span>
              </td>
              <td className="px-md py-sm break-words">
                {p.b.label}
                <span className="block text-xs text-textSecondary">
                  {p.b.questionLabel}
                </span>
              </td>
              <td className="px-md py-sm text-right tabular-nums whitespace-nowrap">
                {formatShare(p.share)}
                <span className="text-textSecondary">
                  {" "}
                  ({formatCell(p.count)})
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function F01InsightsView({ report }: { report: F01InsightReport }) {
  const web = report.population === "web_unidentified";
  return (
    <div className="flex flex-col gap-lg">
      <div className="grid gap-sm grid-cols-2 md:grid-cols-3">
        <div className="border border-surfaceMuted rounded-md p-md bg-surface flex flex-col gap-xs">
          <p className="text-xs uppercase tracking-wide text-textSecondary">
            {web ? "Webbsvar utan identifiering" : "Har genomfört F-01"}
          </p>
          <p className="font-display text-3xl tabular-nums">
            {formatCell(report.respondents)}
          </p>
          <p className="text-xs text-textSecondary">
            {web
              ? "Varje webbsvar räknas för sig. Svaren saknar identitet."
              : "En person räknas en gång, med sina senaste svar."}
          </p>
        </div>
        {report.groups
          .filter((g) => g.key !== "all")
          .map((g) => (
            <div
              key={g.key}
              className="border border-surfaceMuted rounded-md p-md bg-surface flex flex-col gap-xs"
            >
              <p className="text-xs uppercase tracking-wide text-textSecondary">
                {g.label}
              </p>
              <p className="font-display text-3xl tabular-nums">
                {formatCell(g.respondents)}
              </p>
            </div>
          ))}
      </div>
      {report.olderVersions.n !== 0 ? (
        <p className="text-xs text-textSecondary">
          Svar på formulärversioner före version 8 räknas inte med (
          {formatCell(report.olderVersions)}): deras svarsalternativ var andra.
        </p>
      ) : null}
      {report.questions.map((q) => (
        <QuestionTable key={q.key} question={q} groups={report.groups} />
      ))}
      <section className="flex flex-col gap-xs">
        <h2 className="text-sm font-medium">
          Svar som ofta förekommer tillsammans
        </h2>
        <p className="text-xs text-textSecondary max-w-xl">
          {web
            ? "De vanligaste paren av två svar, bland alla webbsvar utan identifiering."
            : "De vanligaste paren av två svar, bland alla som genomfört F-01."}
        </p>
        <Pairs pairs={report.pairs} />
      </section>
      <Rules report={report} />
    </div>
  );
}

export function F01InsightsRoute() {
  const [period, setPeriod] = useState("all");
  const [breakdown, setBreakdown] = useState<F01InsightBreakdown>("cohort");
  const [population, setPopulation] =
    useState<F01InsightPopulation>("patients");
  const [data, setData] = useState<F01InsightReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setData(null);
    try {
      setData(await fetchF01Insights(period, breakdown, population));
    } catch (err) {
      if (err instanceof F01InsightsDisabledError) {
        setDisabled(true);
        return;
      }
      setError(err instanceof Error ? err.message : "okänt fel");
    }
  }, [period, breakdown, population]);

  // A population brings its own breakdowns: the web answers have no funnel
  // step, so switching to them leaves "Steg i patientresan" for the first
  // one they allow. Periods differ too, so the period goes back to all.
  const choosePopulation = (next: F01InsightPopulation) => {
    const allowed = F01_INSIGHT_POPULATION_BREAKDOWNS[next];
    if (!allowed.includes(breakdown)) setBreakdown(allowed[0]);
    setPeriod("all");
    setPopulation(next);
  };

  useEffect(() => {
    void load();
  }, [load]);

  const header = (
    <div className="flex items-start justify-between gap-md flex-wrap">
      <div className="flex flex-col gap-xs">
        <h1 className="font-display text-3xl tracking-tight">
          F-01-insikter
          {population === "web_unidentified" ? (
            <span className="ml-sm align-middle text-xs font-sans px-sm py-xs rounded-full border border-surfaceMuted">
              {F01_INSIGHT_POPULATION_LABELS.web_unidentified}
            </span>
          ) : null}
          {data?.mode === "synthetic" ? (
            <span className="ml-sm align-middle text-xs font-sans px-sm py-xs rounded-full border border-brand bg-brand/10">
              Syntetiska data
            </span>
          ) : null}
        </h1>
        <p className="text-textSecondary text-sm max-w-xl">
          Vad de som fyllt i F-01 svarat, i grupp. Underlag för att förbättra
          vårdtjänsten, patientresan och patientinformationen.
        </p>
      </div>
      {data ? (
        <div className="flex gap-sm flex-wrap items-center shrink-0">
          {data.populations.length > 1 ? (
            <div className="flex gap-xs" role="group" aria-label="Underlag">
              {data.populations.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => choosePopulation(p)}
                  aria-pressed={population === p}
                  className={`px-md py-sm rounded-md border text-sm ${
                    population === p
                      ? "border-brand bg-brand/10"
                      : "border-surfaceMuted"
                  }`}
                >
                  {F01_INSIGHT_POPULATION_LABELS[p]}
                </button>
              ))}
            </div>
          ) : null}
          <label className="text-sm flex items-center gap-xs">
            Period
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="px-sm py-xs rounded-md border border-surfaceMuted bg-surface"
            >
              {data.periods.map((p) => (
                <option key={p} value={p}>
                  {periodLabel(p)}
                </option>
              ))}
            </select>
          </label>
          <div className="flex gap-xs" role="group" aria-label="Jämförelse">
            {F01_INSIGHT_POPULATION_BREAKDOWNS[population].map((b) => (
              <button
                key={b}
                type="button"
                onClick={() => setBreakdown(b)}
                aria-pressed={breakdown === b}
                className={`px-md py-sm rounded-md border text-sm ${
                  breakdown === b
                    ? "border-brand bg-brand/10"
                    : "border-surfaceMuted"
                }`}
              >
                {BREAKDOWN_LABELS[b]}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );

  if (disabled) {
    return (
      <div className="flex flex-col gap-lg">
        {header}
        <p className="text-sm text-textSecondary border border-surfaceMuted rounded-md p-md bg-surface max-w-xl">
          F-01-insikterna är inte påslagna i den här miljön. De slås på när
          dokumentationen, behörigheterna och anonymiseringstesterna är
          godkända.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-lg">
        {header}
        <div className="border border-danger rounded-md p-md text-sm bg-surface flex flex-col gap-sm items-start">
          <p className="text-danger">Insikterna kunde inte hämtas ({error}).</p>
          <button
            type="button"
            onClick={() => void load()}
            className="px-md py-sm rounded-md border border-surfaceMuted"
          >
            Försök igen
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col gap-lg">
        {header}
        <div
          className="grid gap-sm grid-cols-2 md:grid-cols-3"
          aria-busy="true"
        >
          {Array.from({ length: 3 }, (_, i) => (
            <div
              key={i}
              className="h-24 rounded-md border border-surfaceMuted bg-surfaceMuted/30 animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-lg">
      {header}
      {data.respondents.n === 0 ? (
        <p className="text-sm text-textSecondary border border-surfaceMuted rounded-md p-md bg-surface">
          {population === "web_unidentified"
            ? "Inga webbsvar utan identifiering under perioden."
            : "Ingen har genomfört F-01 under perioden."}
        </p>
      ) : (
        <F01InsightsView report={data} />
      )}
    </div>
  );
}
